import React, {lazy, Suspense, useEffect, useRef, useState} from 'react';
import { Helmet } from 'react-helmet';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { Element } from 'react-scroll';
import { CSSTransition } from 'react-transition-group';
import styled, { createGlobalStyle } from 'styled-components';
import Images from '../../../../common/Images';
import LoadingScreen from '../../../../common/LoadingScreen';
import useHover from '../../../../hooks/useHover';
import useMedia from '../../../../hooks/useMedia';
import { handleGlobalCart } from '@/store/actions';
import { getName, getSection } from '@/utils/helper';
import NoMatch from '../../../noMatch/NoMatch';
import {
  createSliderImages,
  getDescriptionForHeader,
  getTitleForHeader
} from '../helper';
import {
  formatSegmentDataForProductViewedEvent,
  segmentTrackEvent
} from '@/utils/segmentTracking';
import useFabricBySlug from '../hooks/useFabricBySlug';
import useFindFabric from '../hooks/useFindFabric';
import useFindProduct from '../hooks/useFindProduct';
import useProductBySlug from '../hooks/useProductBySlug';
import FabricSidebar from './fabricSidebar/FabricSidebar';
import HowToCalculateWallpaperSlider from './howToCalculateWallpaperSlider/HowToCalculateWallpaperSlider';
import ImageSlider from './ImageSlider/ImageSlider';
import JsonLDProduct from './SchemaOrg/JsonLDProduct';
import Sidebar from './sidebar/Sidebar';
import ValueProposition from './valueProposition/ValueProposition';
import ViewDetailSidebar from './viewDetailSidebar/ViewDetailSidebar';
import Categories from '@/constants/categories';
import { ShippingFilterState } from '@/components/customers/product/productBody/sidebar/QuickShipSwitch';
import useQueryParams from "@/hooks/useQueryParams";

const Review = lazy(() => import('../review/Review'));
const QuestionsAndAnswers = lazy(() =>
  import('./questionsAndAnswers/QuestionsAndAnswers')
);

const PetiteSlipperChairGuide = lazy(() =>
  import('./petiteSlipperChairGuide/PetiteSlipperChairGuide')
);
const RugSizesGuide = lazy(() => import('./rugSizesGuide/RugSizesGuide'));
const ShopTheLookV2 = lazy(() => import('./shopTheLookV2/ShopTheLook'));

const ShopTheLook = lazy(() => import('./shopTheLook/ShopTheLook'));

const Ugc = lazy(() => import('./ugc/Ugc'));
const OurStory = lazy(() => import('./ourStory/OurStory'));
const GoInside = lazy(() => import('./goInside/GoInside'));

const Wrapper = styled.div`
  .main-product-body {
    margin: 0 auto;
    display: grid;
    grid-template-columns: minmax(0, 1fr) 420px;
    border-bottom: 1px solid #d8d8d8;

    .image-side {
      padding-bottom: 15px;
      background: rgb(248, 248, 248);
    }

    .image-slider-container {
      height: fit-content;
      position: sticky !important;
      top: 0;
    }
  }

  .menu-fabric-enter {
    transform: translateX(100%);
  }

  .menu-view-fabric-enter {
    transform: translateX(100%);
  }

  .menu-fabric-enter-active {
    transform: translateX(100%);
    transition: transform 0.3s ease-in-out;
  }

  .menu-view-fabric-enter-active {
    transform: translateX(100%);
    transition: transform 0.3s ease-in-out;
  }

  .menu-fabric-enter-done {
    transform: translateX(0%);
    transition: transform 0.3s ease-in-out;
  }

  .menu-view-fabric-enter-done {
    transform: translateX(0%);
    transition: transform 0.3s ease-in-out;
  }

  .menu-fabric-exit {
    transform: translateX(100%);
    transition: transform 0.3s ease-in-out;
  }

  .menu-fabric-exit {
    transform: translateX(100%);
    transition: transform 0.3s ease-in-out;
  }

  .menu-fabric-exit-active {
    transform: translateX(100%);
    transition: transform 0.3s ease-in-out;
  }
  .menu-view-fabric-exit-active {
    transform: translateX(100%);
    transition: transform 0.3s ease-in-out;
  }

  /* Side cart transitions*/

  .side-cart-enter {
    transform: translateX(100%);
  }
  .side-cart-enter-active {
    transform: translateX(100%);
    transition: transform 0.3s ease-in-out;
  }

  .side-cart-exit-active {
    transform: translateX(100%);
    transition: transform 0.3s ease-in-out;
  }

  .side-cart-enter-done {
    transform: translateX(0);
    transition: transform 0.3s ease-in-out;
  }

  /* Menu Slide Down transitions */

  .menu-fabric-mobile-enter {
    transform: translateY(calc(-100%));
  }
  .menu-fabric-mobile-enter-active {
    transform: translateY(calc(-100%));
    transition: transform 0.3s ease-in-out;
  }

  .menu-fabric-mobile-exit-active {
    transform: translateY(calc(-100%));
    transition: transform 0.3s ease-in-out;
  }

  .menu-fabric-mobile-enter-done {
    transform: translateY(calc(0%));
    transition: transform 0.3s ease-in-out;
  }

  @media (max-width: 992px) {
    padding-bottom: 0;
    .main-product-body {
      display: block;
      .image-slider-container {
        display: none;
      }
    }
  }
`;

const BreadCrumb = styled.div`
  margin-left: 20px;
  display: flex;
  flex-flow: wrap;
  padding: 15px 0;

  a {
    margin-top: 10px;
  }
  a,
  .frame-name {
    font-family: ${({ theme }) => theme.fonts.primary};
    font-style: normal;
    font-weight: 500;
    font-size: 13px;
    line-height: 23px;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: #6c6c6c;
    margin-right: 4px;
    &:hover {
      text-decoration: none;
    }
  }

  .frame-name {
    color: ${({ theme }) => theme.colors.charcoal};
  }

  @media (max-width: 992px) {
    a,
    .frame-name {
      line-height: 16px;
    }
  }
`;

const GlobalStyle = createGlobalStyle`
  html > body {
    overflow: ${({ open }) => (open ? 'hidden' : 'auto')};
    overflow-x: hidden;
  }

  .extole-overlay-cta-wrapper {
    display: ${({ open }) => (open ? 'none' : 'auto')};
  }

  #gorgias-chat-container {
    display: ${({ open }) => (open ? 'none' : 'auto')};
  }

  #nav-banner-mobile {
    visibility: ${({ open }) => (open ? 'hidden' : 'visible')};
    display: ${({ open }) => (open ? 'none' : 'auto')};
    transition: all 1s;
  }
`;

const initialState = {
  id: '',
  name: '',
  image: '',
  description: null,
  material: {
    composition: '',
    durability: ''
  },
  collaborator: {
    name: ''
  },
  pattern: {
    name: ''
  }
};

function ProductBody() {
  const dispatch = useDispatch();
  const params = useQueryParams();
  const sliderRef = useRef(null);

  const quickShipParam = false;

  const [showFabric, setShowFabric] = useState(false);
  const [showHowToWallpaper, setShowHowToWallpaper] = useState(false);
  const [viewDetails, setViewDetails] = useState(false);
  const [hoveredFabric, setHoveredFabric] = useState(initialState);
  const [productVar, setProductVar] = useState({
    category_id: '',
    fabric_id: '',
    size_id: '',
    firmness_id: '',
    leg_id: '',
    caster_leg_id: '',
    orientation: '',
    wallpaper_type: '',
    pillow_insert_id: '',
    lining_id: '',
    constructionType_id: ''
  });
  const [selectedFabric, setSelectedFabric] = useState({
    name: null,
    id: ''
  });
  const [shippingFilter, setShippingFilter] = useState(
    quickShipParam
      ? ShippingFilterState.QUICK_SHIP
      : ShippingFilterState.MADE_TO_ORDER
  );

  const handleToggleSideCart = ({ sku, productData }) => {
    dispatch(handleGlobalCart({ sku: sku, productData: productData }));
  };

  const [hoverRefImage, isImageHovered] = useHover();

  const isMobile =
    typeof window !== `undefined` ? useMedia('(max-width: 992px) ') : null;

  const [changeFeaturedFabrics, setChangeFeaturedFabrics] = useState(false);
  const [featuredFabrics, setFeaturedFabrics] = useState(false);

  const { loading, error, data } = useProductBySlug(
    setFeaturedFabrics,
    changeFeaturedFabrics
  );


  const { loading: loadingFabrics, data: dataFabrics } = useFabricBySlug();
  const { loading: loadingOption, error: errorFindProduct } = useFindProduct(
    productVar,
    changeFeaturedFabrics,
    setChangeFeaturedFabrics,
    setFeaturedFabrics
  );
  useFindFabric(productVar);

  useEffect(() => {
    if (!loading && !error && data?.productBySlug) {
      segmentTrackEvent('Product Viewed', {
        ...formatSegmentDataForProductViewedEvent(data.productBySlug)
      });
    }
  }, [data, loading, error]);

  const handleShowFabric = () => {
    setShowFabric(!showFabric);
  };

  if (loading)
    return (
      <LoadingContainer>
        <LoadingScreen />
      </LoadingContainer>
    );

  if (error) return <NoMatch />;

  const sliderImages = createSliderImages(data.productBySlug);

  const [
    isAccentTable,
    isNightstand,
    isIndoorRug,
    isLighting,
    isOutdoorRug,
    isBedding
  ] = getCategoryBelongsTo(data);
  const categoryName = data.productBySlug.frame.subcategories[0].category.name;

  const isArtDecoBedSubcategory =
    data.productBySlug?.frame?.primarySubcategory?.id == 5;

  const rugCategories = [30, 31].includes(
    parseInt(data.productBySlug?.frame.subcategories[0].category.id)
  );

  const categorySlug = data.productBySlug?.frame.subcategories[0].category.slug;
  const supplierId = data.productBySlug.supplier.id;
  const section = getSection(categorySlug);

  const getCategoryLink = () => {
    if (isIndoorRug) {
      return '/collections/rugs';
    }

    if (isLighting) {
      return '/collections/lighting';
    }

    if (isOutdoorRug) {
      return '/collections/outdoor-rugs';
    }

    if (isNightstand) {
      return '/collections/nightstands';
    }

    if (isAccentTable) {
      return '/collections/accent-tables';
    }

    if (isBedding) {
      return '/collections/all-bedding';
    }

    return `/c${section}/${categorySlug}`;
  };

  const getFrameLink = () => {
    if (isIndoorRug) {
      return '/collections/rugs';
    }

    if (isOutdoorRug) {
      return '/collections/outdoor-rugs';
    }

    if (isNightstand) {
      return '/collections/nightstands';
    }

    if (isAccentTable) {
      return '/collections/accent-tables';
    }

    return `/c${section}/${categorySlug}/${data.productBySlug.frame.slug}`;
  };

  const pillowScapingFrames =
    data.productBySlug?.frame?.pillowscapingFrames?.length > 2;
  const pillowScapingIcon = data.productBySlug?.frame?.pillowscaping_icon;

  const isPillowscaping = !!(pillowScapingFrames && pillowScapingIcon);

  const isPetiteSliderChairFrame = data.productBySlug?.frame?.id == 558;

  const goToDimensionsImageSlide = () => {
    const index = sliderImages.findIndex(image => image.name == 'dimensions');
    if (index > -1) {
      sliderRef?.current?.slickGoTo(index);
    }
  };

  return (
    <Wrapper className="product-body">
      {data.productBySlug.id && (
        <>
          <Helmet>
            <title>{getTitleForHeader(data.productBySlug)}</title>

            <meta
              name="description"
              content={getDescriptionForHeader(data.productBySlug)}
            />
          </Helmet>

          <JsonLDProduct product={data.productBySlug} />
        </>
      )}

      <div className="main-product-body">
        <div className="image-side">
          <BreadCrumb>
            <Link to="/">Custom Furniture Home / </Link>{' '}
            <Link to={getCategoryLink()}>
              {!isIndoorRug &&
              !isOutdoorRug &&
              !isAccentTable &&
              !isNightstand &&
              !isLighting
                ? 'Custom'
                : ''}{' '}
              {categoryName} /{' '}
            </Link>{' '}
            <Link to={getFrameLink()}>
              <div className="frame-name">
                {getName(data.productBySlug.name)}
              </div>
            </Link>
          </BreadCrumb>

          <div className="image-slider-container">
            {!isMobile && (
              <ImageSlider
                productData={data.productBySlug}
                hoverRefImage={hoverRefImage}
                sliderRef={sliderRef}
                sliderImages={sliderImages}
                loading={loadingOption}
              />
            )}
          </div>
        </div>
        <Sidebar
          goToDimensionsImageSlide={goToDimensionsImageSlide}
          setShowHowToWallpaper={setShowHowToWallpaper}
          errorFindProduct={errorFindProduct}
          handleToggleSideCart={handleToggleSideCart}
          setChangeFeaturedFabrics={setChangeFeaturedFabrics}
          featuredFabrics={featuredFabrics}
          loadingFabrics={loadingFabrics}
          dataFabrics={dataFabrics}
          setProductVar={setProductVar}
          productVar={productVar}
          isMobile={isMobile}
          handleShowFabric={handleShowFabric}
          hoveredFabric={hoveredFabric}
          setHoveredFabric={setHoveredFabric}
          sliderImages={sliderImages}
          loading={loadingOption}
          productData={data.productBySlug}
          shippingFilter={shippingFilter}
          setShippingFilter={setShippingFilter}
        />

        <CSSTransition
          in={showFabric}
          timeout={300}
          classNames="menu-fabric"
          unmountOnExit
        >
          <FabricSidebar
            isImageHovered={isImageHovered}
            viewDetails={viewDetails}
            pdpFabrics={dataFabrics?.productBySlug}
            setChangeFeaturedFabrics={setChangeFeaturedFabrics}
            loading={loadingOption}
            productData={data.productBySlug}
            setProductVar={setProductVar}
            selectedFabric={selectedFabric}
            setSelectedFabric={setSelectedFabric}
            handleShowFabric={handleShowFabric}
            shippingFilter={shippingFilter}
            open={showFabric}
            setHoveredFabric={setHoveredFabric}
            sliderImages={sliderImages}
            setViewDetails={setViewDetails}
          />
        </CSSTransition>

        <CSSTransition
          in={showHowToWallpaper}
          timeout={300}
          classNames="menu-fabric"
          unmountOnExit
        >
          <HowToCalculateWallpaperSlider
            handleToggleSideCart={handleToggleSideCart}
            productData={data.productBySlug}
            handleOpenWallpaper={setShowHowToWallpaper}
            open={showHowToWallpaper}
          />
        </CSSTransition>

        <CSSTransition
          in={viewDetails}
          timeout={300}
          classNames="menu-view-fabric"
          unmountOnExit
        >
          <ViewDetailSidebar
            viewDetails={viewDetails}
            setViewDetails={setViewDetails}
            selectedFabric={selectedFabric}
            productData={!loadingOption && data.productBySlug}
          />
        </CSSTransition>

        {showFabric || showHowToWallpaper ? (
          <GlobalStyle open={showFabric || showHowToWallpaper} />
        ) : (
          ''
        )}
      </div>

      <ValueProposition
        className="hide-on-mobile"
        productData={data.productBySlug}
      />

      {rugCategories ? (
        <Suspense fallback={<LoadingScreen />}>
          <RugSizesGuide isOutdoorRug={isOutdoorRug} />
        </Suspense>
      ) : (
        ''
      )}

      {isPetiteSliderChairFrame ? (
        <Suspense fallback={<LoadingScreen />}>
          <PetiteSlipperChairGuide />
        </Suspense>
      ) : (
        ''
      )}

      <Suspense fallback={<LoadingScreen />}>
        <Ugc
          productData={data.productBySlug}
          setProductVar={setProductVar}
          setSelectedFabric={setSelectedFabric}
          setChangeFeaturedFabrics={setChangeFeaturedFabrics}
          subCategoryId={data.productBySlug.frame.subcategories[0].id}
        />
      </Suspense>

      {isArtDecoBedSubcategory && (
        <Suspense fallback={<LoadingScreen />}>
          <GoInside
            productData={data.productBySlug}
            setProductVar={setProductVar}
            setSelectedFabric={setSelectedFabric}
            setChangeFeaturedFabrics={setChangeFeaturedFabrics}
          />
        </Suspense>
      )}

      {/* <FeaturedVideo /> */}

      <Suspense fallback={<LoadingScreen />}>
        <OurStory supplierId={parseInt(supplierId)} />
      </Suspense>

      {data.productBySlug.fabric ? (
        <Suspense fallback={<LoadingScreen />}>
          <ShopTheLookV2
            setChangeFeaturedFabrics={setChangeFeaturedFabrics}
            handleToggleSideCart={handleToggleSideCart}
            productData={data.productBySlug}
          />
        </Suspense>
      ) : (
        ''
      )}

      {isPillowscaping ? (
        <Suspense fallback={<LoadingScreen />}>
          <ShopTheLook
            setChangeFeaturedFabrics={setChangeFeaturedFabrics}
            handleToggleSideCart={handleToggleSideCart}
            productData={data.productBySlug}
          />
        </Suspense>
      ) : (
        ''
      )}

      <Suspense fallback={<LoadingScreen />}>
        <QuestionsAndAnswers
          subCategoryId={data.productBySlug.frame.subcategories[0].id}
          productId={data.productBySlug.id}
        />
      </Suspense>
      <Element name={'reviews-place'} className="element">
        <Suspense fallback={<LoadingScreen />}>
          <Review
            frame={data.productBySlug.frame}
            productId={data.productBySlug.id}
          />
        </Suspense>
      </Element>

      {/* Instagram feed */}
      {/*<Images />*/}
    </Wrapper>
  );
}

function getCategoryBelongsTo(data) {
  const empty = Array(5).fill(false);

  if (!data.productBySlug) return empty;

  const categoryId = data.productBySlug.frame?.subcategories[0]?.category?.id;

  if (!categoryId) return empty;

  return [
    categoryId == Categories.ACCENT_TABLES,
    categoryId == Categories.NIGHTSTANDS,
    categoryId == Categories.INDOOR_RUGS,
    categoryId == Categories.LIGHTING,
    categoryId == Categories.OUTDOOR_RUGS,
    categoryId == Categories.BEDDING
  ];
}

export default ProductBody;

const LoadingContainer = styled.div`
  min-height: 600px;
`;
